import { api } from './client';
import { normaliseStatus } from '../lib/statusMapping';

export type RequirementStatus =
  | 'INTAKE'
  | 'TRIAGE'
  | 'REFINING'
  | 'CLARIFICATION'
  | 'READY_FOR_RELEASE'
  | 'RELEASED'
  | 'ARCHIVED';

export type RequirementClassification = 'BUG' | 'USER_STORY' | 'UNKNOWN';

export type FinalAction = 'TICKET_CREATED' | 'EXPORTED';

export type Attachment = {
  id: string;
  filename: string;
  mimetype: string;
  sizeBytes: number;
  createdAt: string;
};

export type RawInput = {
  title?: string;
  description?: string;
  customer?: string;
  project?: string;
  context?: string;
  [key: string]: unknown;
};

export type Requirement = {
  id: string;
  createdAt: string;
  updatedAt: string;
  rawInput: RawInput | null;
  customer?: string | null;
  project?: string | null;
  context?: string | null;
  classification: RequirementClassification;
  summary?: string | null;
  structuredOutput?: unknown;
  missingInfo?: string[] | null;
  status: RequirementStatus;
  reviewNotes?: string | null;
  finalAction?: FinalAction | null;
  markdownExportedAt?: string | null;
  lastError?: string | null;
  lastErrorAt?: string | null;
  attachments: Attachment[];
};

export type CreateRequirementInput = {
  title: string;
  description: string;
  customer?: string;
  project?: string;
  context?: string;
  files?: File[];
};

export type UpdateRequirementInput = {
  classification?: RequirementClassification;
  customer?: string;
  project?: string;
  context?: string;
  summary?: string;
  reviewNotes?: string;
};

function canonicalise(r: Requirement): Requirement {
  const next = normaliseStatus(r.status);
  if (!next || next === r.status) return r;
  return { ...r, status: next };
}

export async function listRequirements(): Promise<Requirement[]> {
  const { data } = await api.get<Requirement[]>('/requirements');
  return data.map(canonicalise);
}

export async function getRequirement(id: string): Promise<Requirement> {
  const { data } = await api.get<Requirement>(`/requirements/${id}`);
  return canonicalise(data);
}

export async function createRequirement(
  input: CreateRequirementInput,
): Promise<Requirement> {
  const hasFiles = !!input.files && input.files.length > 0;
  if (hasFiles) {
    const form = new FormData();
    form.append('title', input.title);
    form.append('description', input.description);
    if (input.customer) form.append('customer', input.customer);
    if (input.project) form.append('project', input.project);
    if (input.context) form.append('context', input.context);
    for (const f of input.files!) form.append('files', f, f.name);
    const { data } = await api.post<Requirement>('/requirements', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return canonicalise(data);
  }
  const { data } = await api.post<Requirement>('/requirements', {
    title: input.title,
    description: input.description,
    customer: input.customer,
    project: input.project,
    context: input.context,
  });
  return canonicalise(data);
}

export async function updateRequirement(
  id: string,
  input: UpdateRequirementInput,
): Promise<Requirement> {
  const { data } = await api.patch<Requirement>(`/requirements/${id}`, input);
  return canonicalise(data);
}

export async function approveClassification(id: string): Promise<Requirement> {
  const { data } = await api.post<Requirement>(
    `/requirements/${id}/approve-classification`,
  );
  return canonicalise(data);
}

export async function approveFinal(id: string): Promise<Requirement> {
  const { data } = await api.post<Requirement>(
    `/requirements/${id}/approve-final`,
  );
  return canonicalise(data);
}

export async function rerefineRequirement(id: string): Promise<Requirement> {
  const { data } = await api.post<Requirement>(`/requirements/${id}/re-refine`);
  return canonicalise(data);
}

export async function revokeClassification(id: string): Promise<Requirement> {
  const { data } = await api.post<Requirement>(
    `/requirements/${id}/revoke-classification`,
  );
  return canonicalise(data);
}

export async function revokeApproval(id: string): Promise<Requirement> {
  const { data } = await api.post<Requirement>(
    `/requirements/${id}/revoke-approval`,
  );
  return canonicalise(data);
}

export async function fetchMarkdown(id: string): Promise<string> {
  const { data } = await api.get<string>(`/requirements/${id}/export.md`, {
    responseType: 'text',
    transformResponse: (raw) => raw,
  });
  return data;
}

export function attachmentUrl(requirementId: string, attachmentId: string): string {
  return `/api/requirements/${requirementId}/attachments/${attachmentId}`;
}

export function markdownDownloadUrl(requirementId: string): string {
  return `/api/requirements/${requirementId}/export.md?download=1`;
}
